import type { Booking } from '@coachapp/shared';
import type { TrainingPlan } from '../store/coachApi';

export type TrainingPlanStatus = 'ACTIVE' | 'ARCHIVED';
export type TrainingPlanTab = 'active' | 'archived';

export const TRAINING_PLAN_DEFAULT_VISIBLE = 5;

export function isActiveTrainingPlan(plan: TrainingPlan): boolean {
  return (plan.status ?? 'ACTIVE') !== 'ARCHIVED';
}

export function filterClientPlans(
  plans: TrainingPlan[],
  clientId: string,
  status?: TrainingPlanStatus,
): TrainingPlan[] {
  return plans.filter((plan) => {
    if (plan.clientId !== clientId) return false;
    if (status && (plan.status ?? 'ACTIVE') !== status) return false;
    return true;
  });
}

export function sortPlansByRecency(plans: TrainingPlan[]): TrainingPlan[] {
  return [...plans].sort((a, b) => {
    const aTime = new Date(a.updatedAt ?? a.createdAt).getTime();
    const bTime = new Date(b.updatedAt ?? b.createdAt).getTime();
    return bTime - aTime;
  });
}

export function buildPlanUsageCounts(bookings: Booking[]): Map<string, number> {
  const counts = new Map<string, number>();

  for (const booking of bookings) {
    if (!booking.trainingPlanId) continue;
    counts.set(
      booking.trainingPlanId,
      (counts.get(booking.trainingPlanId) ?? 0) + 1,
    );
  }

  return counts;
}

export function getMonthGroupKey(date: string): string {
  const parsed = new Date(date);
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
}

export function formatMonthGroupLabel(
  date: string,
  locale?: string,
): string {
  return new Date(date).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
}

export function groupPlansByMonth(
  plans: TrainingPlan[],
  locale?: string,
): Array<{ key: string; label: string; plans: TrainingPlan[] }> {
  const sorted = sortPlansByRecency(plans);
  const groups = new Map<string, TrainingPlan[]>();

  for (const plan of sorted) {
    const key = getMonthGroupKey(plan.createdAt);
    const existing = groups.get(key);
    if (existing) {
      existing.push(plan);
    } else {
      groups.set(key, [plan]);
    }
  }

  return Array.from(groups.entries()).map(([key, groupPlans]) => ({
    key,
    label: formatMonthGroupLabel(groupPlans[0].createdAt, locale),
    plans: groupPlans,
  }));
}

export function sliceVisiblePlans(
  plans: TrainingPlan[],
  expanded: boolean,
  limit = TRAINING_PLAN_DEFAULT_VISIBLE,
): { visible: TrainingPlan[]; hiddenCount: number } {
  if (expanded || plans.length <= limit) {
    return { visible: plans, hiddenCount: 0 };
  }

  return {
    visible: plans.slice(0, limit),
    hiddenCount: plans.length - limit,
  };
}

export function getRecentlyUsedPlanIds(
  bookings: Booking[],
  clientId: string,
  limit = 3,
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  const sorted = [...bookings]
    .filter((booking) => booking.clientId === clientId && booking.trainingPlanId)
    .sort(
      (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime(),
    );

  for (const booking of sorted) {
    const planId = booking.trainingPlanId!;
    if (seen.has(planId)) continue;
    seen.add(planId);
    result.push(planId);
    if (result.length >= limit) break;
  }

  return result;
}
